import { useCallback, useEffect, useState, useMemo, memo, useContext } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import {
  Navigation,
  NavBar,
  Container,
  Select,
  FormFooter,
  DatePicker,
  FieldLabel,
  TextInputPlain,
  FieldTipText,
  DateUtils,
} from '../../components';
import { THEME, COLOR, __WEB__, FONT } from '../../../constants/styles';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { resetStatus, getAppointmentsSchedule, saveAppointmentsSchedule } from '../scheduler/slice';
import { getCurrentEmployer } from '../../../selectors/entities';
import AddDeleteSelectSchedule from './provider_schedules/add_delete_select_schedule';
import Days from '../../components/schedule_cal/day_select';
import moment from 'moment';

const NUM_OPTIONS = Array.from({ length: 50 }, (_, i) => {
  const id = i + 1;
  return { label: id, id };
});
const DURATION_OPTIONS = Array(12)
  .fill()
  .map((element, index) => (index + 1) * 5)
  .map((id) => ({ label: `${id} Minutes`, id }));

const PHAProvidersForm = memo((props) => {
  useEffect(NavBar.setup(props), []);
  useEffect(function () {
    props.actions.getAppointmentsSchedule({
      employerId: props.employer?.employerId,
      eventType: props.eventType,
    });
    if (!__WEB__) {
      Navigation.setButtons(
        {
          leftButtons: [NavBar.buttons.done],
        },
        props.componentId
      );
    }
    return function () {
      props.actions.resetStatus();
    };
  }, []);

  const [numProviders, setNumProviders] = useState(1);
  const [selectedDays, setSelectedDays] = useState({});
  const [startDate, setStartDate] = useState(moment().startOf('day').toDate());
  const [endDate, setEndDate] = useState(null);
  const [duration, setDuration] = useState(30);
  const [slotsText, setSlotsText] = useState('');
  const [validateError, setValidateError] = useState(false);
  const [name, setName] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [confirmationOpts, setConfirmationOpts] = useState(null);
  const [currentContainerFocus, setCurrentContainerFocus] = useState(null);

  const orderedSchedules = useMemo(() => {
    return props.schedules?.slice?.()?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
  }, [props.schedules]);

  const selectedSchedule = useMemo(() => {
    return orderedSchedules?.find((s) => s.employerSchedulerHoursId === selectedScheduleId) || null;
  }, [orderedSchedules, selectedScheduleId]);

  useEffect(() => {
    setSelectedScheduleId(
      orderedSchedules?.find((s) => s.name === name)?.employerSchedulerHoursId ||
        orderedSchedules?.[0]?.employerSchedulerHoursId ||
        null
    );
  }, [orderedSchedules]);

  const resetSchedule = useCallback(() => {
    setSelectedScheduleId(orderedSchedules?.[0]?.employerSchedulerHoursId || null);
    setName(null);
  }, [orderedSchedules]);

  const resetScheduleName = useCallback(() => {
    setName(selectedSchedule?.name);
  }, [selectedSchedule]);

  useEffect(() => {
    if (startDate && endDate && moment(startDate).isAfter(endDate)) {
      setEndDate(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (selectedSchedule) {
      setName(selectedSchedule.name || 'New schedule...');
      setStartDate(moment(selectedSchedule.startDate).toDate());
      setEndDate(selectedSchedule.endDate ? moment(selectedSchedule.endDate).toDate() : null);
      setNumProviders(selectedSchedule.numProviders || 1);
      setDuration(selectedSchedule.duration || 30);
      setSlotsText(selectedSchedule.slots?.length ? selectedSchedule.slots.join(', ') : '');
      const newSelectedDays = {};
      selectedSchedule.days.forEach((day) => {
        newSelectedDays[day] = true;
      });
      setSelectedDays(newSelectedDays);
    } else {
      setName(null);
      setStartDate(moment().startOf('day').toDate());
      setEndDate(null);
      setNumProviders(1);
      setDuration(30);
      setSlotsText('');
      setSelectedDays({});
    }
  }, [selectedSchedule]);

  const handleDayPress = useCallback(
    (i) => {
      const newSelectedDays = {
        ...selectedDays,
        [i]: !selectedDays?.[i],
      };
      setSelectedDays(newSelectedDays);
    },
    [selectedDays]
  );

  const formatAndValidateSlots = useCallback(() => {
    if (!slotsText?.length) return [];

    let isValid = true;
    const slots = Array.from(
      new Set(
        slotsText
          .split(',')
          .map((t) => {
            t = t.trim();
            if (!t) return null;

            const parts = t.split(':');
            if (parts.length !== 2) {
              isValid = false;
              return t;
            }

            const hours = parts[0].trim();
            const mins = parts[1].trim();
            if (isNaN(hours) || parseInt(hours) < 0 || parseInt(hours) > 23) isValid = false;
            if (isNaN(mins) || parseInt(mins) < 0 || parseInt(mins) > 59) isValid = false;
            return `${DateUtils.pad(hours)}:${mins}`;
          })
          .filter((t) => t !== null)
      )
    );
    if (!isValid) {
      setValidateError(true);
      return null;
    }
    if (validateError) {
      setValidateError(false);
    }
    return slots;
  }, [slotsText, validateError]);

  const days = Object.keys(selectedDays)
    .filter((day) => !!selectedDays[day])
    .map((d) => parseInt(d));
  const handleCancel = useCallback(() => Navigation.close(props));
  const handleSave = useCallback(() => {
    const slots = formatAndValidateSlots();
    if (!slots) {
      return;
    }

    const schedule = {
      scheduleType: props.eventType,
      name,
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
      numProviders,
      duration,
      slots,
      days,
      employerSchedulerHoursId: selectedScheduleId,
    };
    props.actions.saveAppointmentsSchedule({
      employerId: props.employer.employerId,
      schedule,
    });
  });

  const deleteScheduleConfirm = useCallback(() => {
    if (!selectedScheduleId) return;
    props.actions.saveAppointmentsSchedule({
      employerId: props.employer.employerId,
      schedule: {
        employerSchedulerHoursId: selectedScheduleId,
        isActive: false,
      },
    });
  }, [selectedScheduleId, props.employer, props.actions]);
  const deleteSchedule = useCallback(() => {
    const confirmationOpts = {
      title: 'Remove this schedule?',
      description:
        'This will remove the schedule but all appointments and appointment requests will remain.',
      confirm: 'Yes, Remove',
      reject: 'Cancel',
      onConfirmed: () => {
        deleteScheduleConfirm();
        setConfirmationOpts(null);
      },
      onCancelled: () => {
        setConfirmationOpts(null);
      },
    };
    setConfirmationOpts(confirmationOpts);
  }, [deleteScheduleConfirm]);

  const handleDatePickerFocus = useCallback(() => {
    setCurrentContainerFocus('datePicker');
  }, []);
  const handleSelectFocus = useCallback(() => {
    setCurrentContainerFocus('select');
  }, []);

  const cancelLabel = props.status === SUCCESS ? 'Done' : 'Cancel';
  const footer = props.canEdit ? (
    <FormFooter
      onCancel={handleCancel}
      cancelLabel={cancelLabel}
      onSave={handleSave}
      saveDisabled={!(name?.trim() || selectedScheduleId) || !days?.length || !slotsText}
    />
  ) : null;

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      contentContainerStyle={styles.containerContent}
      renderActivityOverlay={props.scheduleDataStatus === REQUESTING || props.status === REQUESTING}
      errorMessage={props.errorMessage?.message}
      successMessage={props.successMessage}
      confirmationOpts={confirmationOpts}
    >
      <View style={styles.contents}>
        <View style={[styles.innerContainer, { zIndex: 1030, flex: 1 }]}>
          {/*<Text style={styles.desc}>{props.desc}</Text>*/}

          <AddDeleteSelectSchedule
            schedules={orderedSchedules}
            selectedSchedule={selectedScheduleId}
            onSelectSchedule={setSelectedScheduleId}
            onChangeScheduleName={setName}
            onResetSchedule={resetSchedule}
            onResetScheduleName={resetScheduleName}
            onDeleteSchedule={deleteSchedule}
            readOnly={!props.canEdit}
            name={name}
          />
        </View>

        <Text style={[styles.headerLabel, { height: 50 }]}>{name || 'New Schedule'}</Text>

        <View
          style={[
            THEME.basicRow,
            styles.innerContainer,
            { zIndex: currentContainerFocus === 'datePicker' ? 1020 : 0 },
          ]}
        >
          <DatePicker
            label="Start Date"
            minDate={new Date()}
            required={true}
            onDate={setStartDate}
            selectedDate={startDate}
            flexibleHeight={true}
            containerStyle={{ flex: 1, marginRight: 10 }}
            nativeModal={true}
            webModal={true}
            mode="date"
            readOnly={!props.canEdit}
            onFocus={handleDatePickerFocus}
          />
          <DatePicker
            label="End Date"
            extraLabel="(Optional)"
            placeholder="None"
            minDate={moment(startDate).toDate()}
            required={false}
            onDate={setEndDate}
            selectedDate={endDate}
            flexibleHeight={true}
            containerStyle={{ flex: 1, marginLeft: 10 }}
            nativeModal={true}
            webModal={true}
            mode="date"
            clearable={true}
            readOnly={!props.canEdit}
            onFocus={handleDatePickerFocus}
          />
        </View>

        <Days
          headerLabel={false}
          onPressDay={!props.readOnly ? handleDayPress : null}
          selectedDays={selectedDays}
          containerStyle={{ borderBottomWidth: 0 }}
          readOnly={!props.canEdit}
        />
        <View
          style={[
            THEME.basicRow,
            styles.innerContainer,
            { zIndex: currentContainerFocus === 'select' ? 1020 : 0 },
          ]}
        >
          <Select
            required={true}
            editable={!!props.canEdit}
            closeOnSelect={true}
            options={NUM_OPTIONS}
            label="Providers"
            value={numProviders}
            onSelect={setNumProviders}
            webMode={true}
            containerStyle={{ flex: 1, marginRight: 10 }}
            nativeContainerStyle={{ flex: 1, marginRight: 10 }}
            onFocus={handleSelectFocus}
          />
          <Select
            required={true}
            editable={!!props.canEdit}
            closeOnSelect={true}
            options={DURATION_OPTIONS}
            label="Appointment Length"
            value={duration}
            onSelect={setDuration}
            webMode={true}
            containerStyle={{ flex: 1, marginLeft: 10 }}
            nativeContainerStyle={{ flex: 1, marginLeft: 10 }}
            onFocus={handleSelectFocus}
          />
        </View>
        <View style={[styles.innerContainer]}>
          <FieldLabel label="Time Slots" required={true} value={true} />
          <View style={styles.inputContainer}>
            <TextInputPlain
              value={slotsText}
              controlled={true}
              multiline={true}
              style={styles.input}
              placeholder={'Ex: 11:00, 11:30, 12:00, 13:00, 15:00, 15:30'}
              placeholderTextColor={'rgb(170,170,170)'}
              onChangeText={setSlotsText}
              editable={!!props.canEdit}
            />
          </View>
          <FieldTipText
            tipText={'Ex: 11:00, 11:30, 12:00, 13:00, 15:00, 15:30'}
            invalidText={'Invalid Value. Ex: 11:00, 11:30, 12:00, 13:00, 15:00, 15:30'}
            validateError={validateError}
          />
        </View>
      </View>
    </Container>
  );
});

const styles = StyleSheet.create({
  headerLabel: {
    backgroundColor: 'rgb(241, 241, 241)',
    ...FONT.regular,
    color: COLOR.text,
    textAlign: 'center',
    fontSize: 16,
    paddingVertical: 15,
    borderColor: 'rgb(228, 228, 228)',
    borderBottomWidth: 1,
    borderTopWidth: 1,
    marginBottom: 12,
  },
  containerContent: {
    // alignItems: 'center',
    // paddingTop: 10,
  },
  switchContainer: {
    alignItems: 'center',
    paddingVertical: 15,
    borderColor: COLOR.accent,
    borderBottomWidth: 1,
  },
  contents: {
    // maxWidth: 500,
    paddingBottom: 30,
  },
  desc: {
    ...THEME.textRegular,
    marginTop: 0,
  },
  innerContainer: {
    paddingHorizontal: 25,
    paddingTop: 15,
    zIndex: 1000,
  },
  inputContainer: {
    height: 80,
    width: '100%',
    marginTop: 7,
    marginBottom: 7,
  },
  input: {
    ...THEME.input,
    ...FONT.regular,
    fontSize: __WEB__ ? 14 : 14,
    height: 80,
    borderWidth: 1.5,
    borderRadius: 10,
    borderColor: 'rgb(170,170,170)',
    backgroundColor: COLOR.contrast,
    padding: 10,
    paddingHorizontal: 15,
  },
});

function mapStateToProps(state, ownProps) {
  return {
    status: state.screens.scheduler.saveScheduleDataStatus,
    scheduleDataStatus: state.screens.scheduler.scheduleDataStatus,
    successMessage: state.screens.scheduler.saveScheduleDataSuccessMessage,
    errorMessage:
      state.screens.scheduler.saveScheduleDataError || state.screens.scheduler.scheduleDataError,
    employer: getCurrentEmployer(state),
    schedules: state.screens.scheduler.scheduleData,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { resetStatus, getAppointmentsSchedule, saveAppointmentsSchedule },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PHAProvidersForm);
